// Import Dependencies
import dayjs from "dayjs";
import clsx from "clsx";
import { formatPhoneNumber, formatPhoneNumberIntl } from "react-phone-number-input";

// Local Imports
import { Avatar, Badge, Circlebar, Button } from "components/ui";
import { useLocaleContext } from "app/contexts/locale/context";
import { colorFromText } from "utils/colorFromText";
import {
  EnvelopeIcon,
  PhoneIcon,
  CalendarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChatBubbleBottomCenterTextIcon,
  UserGroupIcon,
  ChatBubbleLeftRightIcon,
  PencilSquareIcon,
  TrashIcon,
  EllipsisVerticalIcon,
} from "@heroicons/react/24/outline";
import { formatNumber } from "utils/formatNumber";
import { getLocalDateTime } from "utils/dateUtils";
import { formatCostAustralian } from "utils/formatCost";

// ----------------------------------------------------------------------

const CrownIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M5 16L3 5L8.5 10L12 4L15.5 10L21 5L19 16H5ZM19 19C19 19.5523 18.5523 20 18 20H6C5.44772 20 5 19.5523 5 19V18H19V19Z" />
  </svg>
);

// Helper to add Australian country code (+61) if phone number has no country code
const ensureCountryCode = (phone) => {
  if (!phone) return "";
  
  let cleaned = String(phone).trim();
  if (!cleaned) return "";

  // If it already starts with '+', we assume it has a country code
  if (cleaned.startsWith("+")) {
    return cleaned;
  }

  const digits = cleaned.replace(/\D/g, "");
  if (!digits) return cleaned;

  // If it starts with 61 and is long enough to be an Australian number with country code
  if (digits.startsWith("61") && digits.length >= 10) {
    return `+${digits}`;
  }

  // If it starts with 0, strip it and prepend +61
  if (digits.startsWith("0")) {
    return `+61${digits.slice(1)}`;
  }

  // Otherwise prepend +61
  return `+61${digits}`;
};

export const collaborators = [
  {
    uid: "1",
    name: "John Doe",
    avatar: undefined,
  },
  {
    uid: "2",
    name: "Emilia Clarke",
    avatar: "/images/200x200.png",
  },
  {
    uid: "3",
    name: "Majid Yahyaei",
    avatar: "/images/200x200.png",
  },
  {
    uid: "4",
    name: "Travis Fuller",
    avatar: undefined,
  },
  {
    uid: "5",
    name: "Alfredo Elliott",
    avatar: "/images/200x200.png",
  },
  {
    uid: "6",
    name: "Henry Curtis",
    avatar: undefined,
  },
  {
    uid: "7",
    name: "Bruce Wayne",
    avatar: undefined,
  },
  {
    uid: "8",
    name: "Matt Reeves",
    avatar: "/images/200x200.png",
  },
  {
    uid: "9",
    name: "Timati Chalamet",
    avatar: "/images/200x200.png",
  },
  {
    uid: "10",
    name: "Lance Tucker",
    avatar: "/images/200x200.png",
  },
  {
    uid: "11",
    name: "Katrina West",
    avatar: "/images/200x200.png",
  },
  {
    uid: "12",
    name: "Samantha Shelton",
    avatar: "/images/200x200.png",
  },
  {
    uid: "13",
    name: "Corey Evans",
    avatar: "/images/200x200.png",
  },
  {
    uid: "14",
    name: "Joe Perkins",
    avatar: "/images/200x200.png",
  },
  {
    uid: "15",
    name: "Henry Cavil",
    avatar: undefined,
  },
];
function getColorProgress(val) {
  if (val === 0) return "neutral";
  if (val === 100) return "success";
  if (val < 10) return "warning";
  if (val < 50) return "info";
  return "primary";
}

function getStatusColor(val) {
  switch (val?.toLowerCase()) {
    case "open":
      return "success";
    case "closed":
      return "neutral";
    case "won":
      return "primary";
    case "lost":
      return "error";
    default:
      return "info";
  }
}

// ----------------------------------------------------------------------

export function ActivityTypeCell({ row, getValue }) {
  const channelId = row.original?.channelId || row.original?.ChannelId;
  const getIcon = () => {
    switch (channelId) {
      case 1: return <EnvelopeIcon className="h-3.5 w-3.5" />;
      case 2: return <ChatBubbleLeftRightIcon className="h-3.5 w-3.5" />;
      case 3: return <PhoneIcon className="h-3.5 w-3.5" />;
      case 4: return <UserGroupIcon className="h-3.5 w-3.5" />;
      case 5: return <CheckCircleIcon className="h-3.5 w-3.5" />;
      case 99: return <ChatBubbleBottomCenterTextIcon className="h-3.5 w-3.5" />;
      default: return <CalendarIcon className="h-3.5 w-3.5" />;
    }
  };

  const getColor = () => {
    switch (channelId) {
      case 5:
        return "bg-blue-100 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400";
      case 2:
        return "bg-green-100 text-green-600 dark:bg-green-500/20 dark:text-green-400";
      case 3:
        return "bg-sky-100 text-sky-600 dark:bg-sky-500/20 dark:text-sky-400";
      case 4:
        return "bg-indigo-100 text-indigo-600 dark:bg-indigo-500/20 dark:text-indigo-400";
      case 1:
        return "bg-rose-100 text-rose-600 dark:bg-rose-500/20 dark:text-rose-400";
      default:
        return "bg-indigo-100 text-indigo-600 dark:bg-indigo-500/20 dark:text-indigo-400";
    }
  };

  const getTitle = () => {
    switch (channelId) {
      case 1: return "Email";
      case 2: return "SMS";
      case 3: return "Phone Call";
      case 4: return "Meeting";
      case 5: return "Site Visit";
      case 99: return "Custom";
      default: return "Activity";
    }
  }

  return (
    <div className="flex items-center gap-2">
      <div className={clsx("flex h-6 w-6 items-center justify-center rounded-full", getColor())}>
        {getIcon()}
      </div>
      <span className="text-sm font-medium text-gray-700 dark:text-dark-100">{getTitle()}</span>
    </div>
  );
}

export function TextCell({ getValue, className }) {
  const val = getValue();
  return (
    <div 
      className={clsx("w-full min-w-0 truncate", className)} 
      title={typeof val === 'string' || typeof val === 'number' ? String(val) : undefined}
    >
      {(val === null || val === undefined || val === "") ? "-" : val}
    </div>
  );
}

export function LeadCell({ getValue, className, keyName }) {
  const value = getValue();
  const format = value ? JSON.parse(value) : null;

  return (
    <div className={clsx("w-full min-w-0 truncate text-sm font-medium text-gray-700 dark:text-dark-100", className)} title={format?.[keyName] || undefined}>
      {format?.[keyName] || "-"}
    </div>
  );
}


export function DateCell({
  getValue,
  className,
  format = "DD/MM/YY hh:mm A",
}) {
  const value = getValue();

  const formattedDate =
    value && value !== "-" && dayjs(value).isValid()
      ? dayjs(value).format(format)
      : "-";

  return (
    <div
      className={clsx("w-full min-w-0 truncate", className)}
      title={formattedDate}
    >
      {formattedDate}
    </div>
  );
}

export function PartnerCell({ row, getValue }) {
  const val = getValue();
  return (
    <div className="flex items-center gap-3">
      <div className="size-9">
        <img
          src={row.original.partner_image || row.original.image}
          alt={val}
          className="h-full w-full"
        />
      </div>
      <p className="dark:text-dark-100 font-medium text-gray-800">{val}</p>
    </div>
  );
}

export function StatusCell({ row, getValue }) {
  const status = getValue ? getValue() : row.original.status;

  if (typeof status === "object" && status !== null) {
    return (
      <Badge color={status.color} className="rounded-full" variant="outlined">
        {status.label}
      </Badge>
    );
  }

  return (
    <Badge
      color={getStatusColor(status)}
      className="rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest"
      variant="soft"
    >
      <div className="flex items-center gap-1.5">{status}</div>
    </Badge>
  );
}

export function CreatedDateCell({ getValue }) {
  const { locale } = useLocaleContext();
  const val = getValue();
  if (!val) return <p>-</p>;
  const localDate = getLocalDateTime(val);
  return (
    <div>
      <p>{dayjs(localDate || val).locale(locale).format("DD/MM/YYYY hh:mm A")}</p>
    </div>
  );
}

export function StartedDateCell({ getValue, row }) {
  const { locale } = useLocaleContext();
  return (
    <div>
      <p>{dayjs(getValue()).locale(locale).format("DD/MM/YYYY")}</p>
      <p className="mt-1 text-xs">
        <span className="dark:text-error-lighter font-semibold text-gray-700">Deadline:</span>{" "}
        {dayjs(row.original.deadline || row.original.due_date)
          .locale(locale)
          .format("DD/MM/YYYY")}
      </p>
    </div>
  );
}

export function ProgressCell({ getValue }) {
  const val = getValue();
  return (
    <Circlebar size={13} strokeWidth={9} value={val} color={getColorProgress(val)}>
      <div className="text-tiny-plus dark:text-dark-100 font-semibold text-gray-800">{val}%</div>
    </Circlebar>
  );
}

export function UserCell({ name, avatar, size = 8, showName = false }) {
  const getUserName = (user) => {
    if (typeof user === "string") return user;
    if (user && typeof user === "object") {
      return user.FullName || user.Name || user.name || "—";
    }
    return "—";
  };

  const getUserAvatar = (user) => {
    if (user && typeof user === "object") {
      return user.avatar || user.Avatar || user.src || user.img || avatar;
    }
    return avatar;
  };

  // Parse/Resolve users
  let parsedUsers = [];

  if (Array.isArray(name)) {
    parsedUsers = name;
  } else if (typeof name === "string") {
    const trimmed = name.trim();
    if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
      try {
        const parsed = JSON.parse(trimmed);
        parsedUsers = Array.isArray(parsed) ? parsed : [parsed];
      } catch (e) {
        parsedUsers = trimmed.split(",").map(n => n.trim()).filter(Boolean);
      }
    } else if (trimmed.includes(",") && !trimmed.startsWith("{")) {
      parsedUsers = trimmed.split(",").map(n => n.trim()).filter(Boolean);
    } else if (trimmed) {
      parsedUsers = [trimmed];
    }
  } else if (name && typeof name === "object") {
    parsedUsers = [name];
  }

  // Filter out invalid/placeholder names
  const users = parsedUsers.filter(u => {
    const n = getUserName(u);
    return n && n !== "—" && n.trim() !== "";
  });

  // If no valid users, return fallback
  if (users.length === 0) {
    return <span className="text-sm text-slate-400 dark:text-dark-300">—</span>;
  }

  // If there are multiple users
  if (users.length > 1) {
    const displayNames = users.map(getUserName).join(", ");
    return (
      <div className="flex items-center gap-2">
        <div className="flex -space-x-2 overflow-visible">
          {users.map((user, idx) => {
            const userName = getUserName(user);
            const userAv = getUserAvatar(user);
            return (
              <Avatar
                key={idx}
                data-tooltip
                data-tooltip-content={userName}
                size={size}
                name={userName}
                src={userAv}
                className=" shadow-none"
                initialColor="auto"
                classNames={{
                  root: "origin-bottom transition-transform hover:z-10 hover:scale-125",
                  display: "text-xs",
                }}
              />
            );
          })}
        </div>
        {showName && (
          <span className="text-sm-plus font-semibold text-slate-700 dark:text-white max-w-[150px] truncate" title={displayNames}>
            {displayNames}
          </span>
        )}
      </div>
    );
  }

  // Single user
  const singleUser = users[0];
  const userName = getUserName(singleUser);
  const userAv = getUserAvatar(singleUser);

  return (
    <div className="flex items-center gap-2.5">
      <Avatar
        data-tooltip
        data-tooltip-content={userName}
        size={size}
        name={userName}
        src={userAv}
        className=" shadow-none"
        initialColor="auto"
        classNames={{
          root: "origin-bottom transition-transform hover:z-10 hover:scale-125",
          display: " text-xs",
        }}
      />
      {showName && (
        <span className="text-sm-plus font-semibold text-slate-700 dark:text-white">{userName}</span>
      )}
    </div>
  );
}

export function ContactCell({ row, getValue }) {
  const phone = getValue();
  const isDuplicate = row?.original?.isDuplicate;
  const matchReason = row?.original?.matchReason;

  const StatusIcon = ({ matched }) => {
    if (matched) {
      return <CheckCircleIcon className="h-3 w-3 text-emerald-500 fill-emerald-50 dark:fill-emerald-900/20" />;
    }
    return <XCircleIcon className="h-3 w-3 text-red-500 fill-red-50 dark:fill-red-900/20" />;
  };

  const normalizedPhone = phone ? ensureCountryCode(phone) : "";

  return (
    <div className="flex items-center gap-2.5 py-1">
      {phone ? (
        <div className="flex items-center gap-1.5 mt-0.5 group cursor-pointer">
          <div className="flex items-center gap-1">
            <PhoneIcon className="h-3.5 w-3.5 text-slate-400 group-hover:text-primary transition-colors" />
            <span className="text-xs font-medium  dark:group-hover:text-dark-200 transition-colors tracking-wide">
              {formatNumber(normalizedPhone) || normalizedPhone}
            </span>
          </div>
          {isDuplicate && matchReason && (
            <StatusIcon matched={matchReason.phone} />
          )}
        </div>
      ) : (
        <span className="text-xs text-slate-400">-</span>
      )}
    </div>
  );
}

export function EmailCell({ getValue }) {
  const email = getValue();
  if (!email) return <span className="text-xs text-slate-400">-</span>;
  return (
    <div className="flex items-center gap-2">
      <div className="flex h-6 w-6 items-center justify-center rounded-md bg-primary/10 text-primary">
        <EnvelopeIcon className="h-3.5 w-3.5" />
      </div>
      <span className="text-xs font-semibold text-black hover:underline cursor-pointer">
        {email}
      </span>
    </div>
  );
}

export function PhoneCell({ getValue }) {
  const phone = getValue();
  if (!phone) return null;
  const normalizedPhone = ensureCountryCode(phone);
  return (
    <div className="flex items-center gap-2">
      <div className="flex h-6 w-6 items-center justify-center rounded-md bg-sky-100 text-sky-600 dark:bg-sky-500/20 dark:text-sky-400">
        <PhoneIcon className="h-3.5 w-3.5" />
      </div>
      <span className="text-sm font-semibold text-primary hover:underline cursor-pointer">
        {formatNumber(normalizedPhone) || normalizedPhone}
      </span>
    </div>
  );
}

export function LastContactedCell({ getValue }) {
  const val = getValue();
  if (!val || val === "-") return <span className="text-xs text-slate-400">-</span>;

  const dateObj = dayjs(val);

  const formattedDate = dateObj.isValid() ? dateObj.format("DD/MM/YY") : "-";

  return (
   <div className="flex items-center gap-1 text-sm font-medium text-slate-700 dark:text-dark-100">
       <TextCell getValue={() => formattedDate} />
    </div>
  );
}


export function NumberCell({ getValue }) {
  const val = getValue();
  return (
    <span className="text-sm font-bold tabular-nums text-slate-900 dark:text-white">
      {(val === null || val === undefined || val === "") ? "-" : val}
    </span>
  );
}

export function PriceCell({ getValue }) {
  const val = getValue();
  return (
    <div className="text-left">
      <span className="font-bold text-slate-900 dark:text-white tabular-nums">{val}</span>
    </div>
  );
}

export function BadgeCell({ getValue, suffix = "" }) {
  const val = getValue();
  if (!val) return <span className="text-xs text-slate-400">-</span>;
  return (
    <Badge variant="soft" color="primary" className="rounded-lg px-2.5 py-1 text-[11px] font-bold tracking-tight">
      {val} {suffix}
    </Badge>
  );
}

export function ColorCell({ getValue }) {
  const color = getValue();
  return (
    <div className="flex items-center justify-center">
      <div
        className="h-2.5 w-2.5 rounded-full ring-2 ring-white dark:ring-dark-700 shadow-sm"
        style={{ backgroundColor: color }}
      />
    </div>
  );
}

export function NameCell({ row, getValue }) {
  const name = getValue();
  const suburb = row.original.suburb;
  const state = row.original.state;
  if (!name) return <span className="text-sm font-medium text-slate-400">-</span>;
  return (
    <div className="flex flex-col py-1">
      <span className="font-semibold text-slate-800 dark:text-dark-50 hover:text-primary transition-colors tracking-tight text-sm leading-tight">
        {name}
      </span>
      {(suburb || state) && (
        <div className="flex items-center gap-1.5 mt-0.5">
          {suburb && <span className="text-[9px] text-slate-400 uppercase font-black tracking-widest">{suburb}</span>}
          {suburb && state && <span className="h-0.5 w-0.5 rounded-full bg-slate-300" />}
          {state && <span className="text-[9px] text-slate-400 uppercase font-black tracking-widest">{state}</span>}
        </div>
      )}
    </div>
  );
}

export function RevenueCell({ row, getValue }) {
  const val = getValue();
  const formattedMax = val ? formatCostAustralian(val) : null;
  const formattedMin = row.original.revenueMin ? formatCostAustralian(row.original.revenueMin) : null;

  if (!formattedMax && !formattedMin) return <span className="text-xs text-slate-400">-</span>;
  return (
    <div className="flex flex-col items-end">
      <span className="font-bold text-slate-900 dark:text-white tracking-tight">
        {formattedMax ? `$${formattedMax}` : "-"}
      </span>
      <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wide">
        Min: {formattedMin ? `$${formattedMin}` : "-"}
      </span>
    </div>
  );
}

export function TagsCell({ row }) {
  const tags = row.original.tags || row.original.Tags || [];

  if (tags.length === 0) return <span className="text-xs text-slate-400">-</span>;

  return (
    <div className="flex w-64 flex-wrap gap-1.5">
      {tags.map((item, i) => {
        const tagName = typeof item === 'object' ? (item.TagName || item.tagName) : item;

        return (
          <div
            key={i}
            className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider shadow-sm border border-primary-100/80 dark:border-primary-500/20 bg-primary-50 text-primary-700 dark:bg-primary-500/10 dark:text-primary-400 transition-all hover:scale-105"
          >
            {tagName}
          </div>
        );
      })}
    </div>
  );
}

export function ActionCell({ row, onEdit, onDelete, onMore }) {
  if (row.original.isDuplicate) return null;
  return (
    <div className="flex items-center justify-end gap-1">
      {onEdit && (
        <Button
          variant="flat"
          color="neutral"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(row);
          }}
          className="size-8 p-0 hover:bg-white dark:hover:bg-dark-600 shadow-soft"
        >
          <PencilSquareIcon className="size-4" />
        </Button>
      )}
      {onDelete && (
        <Button
          variant="flat"
          color="error"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(row);
          }}
          className="size-8 p-0 hover:bg-white dark:hover:bg-dark-600 shadow-soft"
        >
          <TrashIcon className="size-4" />
        </Button>
      )}
      {onMore && (
        <Button
          variant="flat"
          color="neutral"
          onClick={(e) => {
            e.stopPropagation();
            onMore(row);
          }}
          className="size-8 p-0 hover:bg-white dark:hover:bg-dark-600 shadow-soft"
        >
          <EllipsisVerticalIcon className="size-4" />
        </Button>
      )}
    </div>
  );
}

export function OwnersCell({ row }) {
  const owners = row?.original?.Owners || row?.Owners || [];

  return (
    <div className="flex -space-x-2">
      {owners.map((owner, i) => {
        const isPrimary = owner.isPrimary;
        const ownerName = owner.OwnerName || "Unknown";

        return (
          <div key={i} className="relative group/avatar">
            <Avatar
              data-tooltip
              data-tooltip-content={ownerName}
              size={8}
              name={ownerName}
              initialColor="auto"
              classNames={{
                root: "origin-bottom transition-transform hover:z-10 hover:scale-125"
              }}
            />
            {isPrimary && (
              <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 z-0 pointer-events-none drop-shadow-lg transition-transform duration-200 group-hover/avatar:-translate-y-2 group-hover/avatar:scale-110">
                <CrownIcon className="h-4 w-4 text-amber-400 fill-amber-400" />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function LeadContactCell({ row, isExpanded }) {
  let leadContact = row.original.LeadContact || row.original.LeadContacts || row?.original._raw?.FollowupContacts;
  if (!leadContact && row.original.leadinfo) {
    try {
      const leadinfo = JSON.parse(row.original.leadinfo);
      leadContact = leadinfo?.LeadContact || leadinfo?.LeadContacts;
    } catch (e) {
      // ignore
    }
  }
  if (typeof leadContact === 'string' && leadContact) {
    try {
      leadContact = JSON.parse(leadContact);
    } catch (e) {
      // ignore
    }
  }

  if (!Array.isArray(leadContact) || leadContact.length === 0) {
    return <div className="text-xs text-slate-400">-</div>;
  }

  const validContacts = leadContact.filter(
    c => c.Fullname || c.FullName || c.fullName || c.PhoneNumber || c.phoneNumber || c.Email || c.email
  );
  if (validContacts.length === 0) {
    return <div className="text-xs text-slate-400">-</div>;
  }

  if (!isExpanded) {
    const contact = validContacts[0];
    const name = contact.Fullname || contact.FullName || contact.fullName || "";
    const phone = contact.PhoneNumber || contact.phoneNumber || "";
    const email = contact.Email || contact.email || "";
    const normalizedPhone = phone ? ensureCountryCode(phone) : "";
    const formattedPhone = normalizedPhone ? formatNumber(normalizedPhone) || normalizedPhone : "";

    return (
      <div className="flex items-center gap-1.5 py-1 w-fit min-w-[140px]">
        <div
          className="flex flex-col justify-center items-start w-fit min-w-[140px] min-h-[46px] px-2.5 py-1 rounded-lg bg-blue-50/60 dark:bg-blue-500/10 border border-blue-200/50 dark:border-blue-500/20 text-[10px] text-blue-700 dark:text-blue-300"
        >
          {name && (
            <div className="flex items-center gap-1 font-bold text-blue-800 dark:text-blue-100 w-full min-w-0">
              <UserGroupIcon className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400 shrink-0" />
              <span className="whitespace-nowrap pb-0.5">{name}</span>
            </div>
          )}
          {formattedPhone && (
            <div className="flex items-center gap-1 text-[9px] text-gray-500 dark:text-white w-full min-w-0">
              <PhoneIcon className="w-2.5 h-2.5 text-gray-400 dark:text-blue-200 shrink-0" />
              <span className="whitespace-nowrap">{formattedPhone}</span>
            </div>
          )}
          {email && (
            <div className="flex items-center gap-1 text-[9px] text-gray-500 dark:text-white w-full min-w-0">
              <EnvelopeIcon className="w-2.5 h-2.5 text-gray-400 dark:text-blue-200 shrink-0" />
              <span className="whitespace-nowrap" title={email}>{email}</span>
            </div>
          )}
        </div>

        {validContacts.length > 1 && (
          <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full bg-slate-100 dark:bg-dark-700 text-[10px] font-bold text-slate-600 dark:text-dark-300 border border-slate-200 dark:border-dark-600 shrink-0">
            +{validContacts.length - 1}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-stretch gap-1.5 py-1 w-fit min-w-[140px]">
      {validContacts.map((contact, idx) => {
        const name = contact.Fullname || contact.FullName || contact.fullName || contact.name || "";
        const phone = contact.PhoneNumber || contact.phoneNumber || "";
        const email = contact.Email || contact.email || "";
        const normalizedPhone = phone ? ensureCountryCode(phone) : "";

        const formattedPhone = normalizedPhone
          ? formatNumber(normalizedPhone) || normalizedPhone
          : "";

        return (
          <div
            key={idx}
            className="flex flex-col justify-center items-start w-fit min-w-[140px] min-h-[46px] px-2.5 py-1 rounded-lg bg-blue-50/60 dark:bg-blue-500/10 border border-blue-200/50 dark:border-blue-500/20 text-[10px] text-blue-700 dark:text-blue-300"
          >
            {name && (
              <div className="flex items-center gap-1 font-bold text-blue-800 dark:text-blue-100 w-full min-w-0">
                <UserGroupIcon className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400 shrink-0" />
                <span className="whitespace-nowrap pb-0.5">{name}</span>
              </div>
            )}
            {formattedPhone && (
              <div className="flex items-center gap-1 text-[9px] text-gray-500 dark:text-white w-full min-w-0">
                <PhoneIcon className="w-2.5 h-2.5 text-gray-400 dark:text-blue-200 shrink-0" />
                <span className="whitespace-nowrap">{formattedPhone}</span>
              </div>
            )}
            {email && (
              <div className="flex items-center gap-1 text-[9px] text-gray-500 dark:text-white w-full min-w-0">
                <EnvelopeIcon className="w-2.5 h-2.5 text-gray-400 dark:text-blue-200 shrink-0" />
                <span className="whitespace-nowrap" title={email}>{email}</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
